import { React, useState, useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "../Home.module.css";
import { EnhancedStockTable } from "../components/EnhancedStockTable/EnhancedStockTable";
import StockFilters from "../components/StockFilters/StockFilters";
import { Snackbar, Alert, Grid } from "@mui/material";
import { CARDS_PER_PAGE, DEFAULT_LISTING_SORT_MODEL, findListingSortOption } from "../utils/listingDefaults";

const FILTER_ARRAY_KEYS = ['categories', 'series', 'manufacturers'];
const FILTER_BOOLEAN_KEYS = ['anyAvailable', 'inStoreAvailable', 'onlineAvailable', 'onSale'];

const parsePositiveInt = (value, fallback) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

// Only the orders on the sort menu are honoured, so a hand-edited or outdated link falls
// back to the page's own order instead of asking the API for something it cannot sort by.
const parseSortModel = (sortParam, initialSortModel) => {
    if (!sortParam) {
        return initialSortModel;
    }

    const [field, sort] = sortParam.split(',');
    const option = findListingSortOption(field, sort);
    return option ? option.model : initialSortModel;
};

const isSameSort = (a, b) => !!a && !!b && a.field === b.field && a.sort === b.sort;

export const HomePage = ({
    initialSearchTerm = "",
    initialFilters = {},
    initialSortModel = DEFAULT_LISTING_SORT_MODEL,
    tableTitle = "Products",
    itemLabel = "products"
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const searchTerm = useMemo(() => (
        searchParams.get('q') ?? initialSearchTerm
    ), [initialSearchTerm, searchParams]);

    const filters = useMemo(() => {
        const nextFilters = { ...initialFilters };

        FILTER_ARRAY_KEYS.forEach((key) => {
            const values = searchParams.getAll(key).filter(Boolean);
            if (values.length > 0) {
                nextFilters[key] = values;
            }
        });

        FILTER_BOOLEAN_KEYS.forEach((key) => {
            if (searchParams.has(key)) {
                nextFilters[key] = searchParams.get(key) === 'true';
            }
        });

        return nextFilters;
    }, [initialFilters, searchParams]);

    // The page size is not a visitor's choice and never comes from the URL: an old link with
    // pageSize=12 would keep 12 forever, and pageSize=9999 flips the API to another shape.
    const page = parsePositiveInt(searchParams.get('page'), 1) - 1;
    const paginationModel = useMemo(() => ({ page, pageSize: CARDS_PER_PAGE }), [page]);

    const sortModel = useMemo(() => (
        parseSortModel(searchParams.get('sort'), initialSortModel)
    ), [initialSortModel, searchParams]);

    const updateSearchParams = useCallback((updater, { replace = true } = {}) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('pageSize');
        updater(nextParams);
        setSearchParams(nextParams, { replace });
    }, [searchParams, setSearchParams]);

    const handleSearch = useCallback((term) => {
        updateSearchParams((params) => {
            if (term) {
                params.set('q', term);
            } else {
                params.delete('q');
            }
            params.delete('page');
        });
    }, [updateSearchParams]);

    const handleFiltersChange = useCallback((newFilters) => {
        updateSearchParams((params) => {
            FILTER_ARRAY_KEYS.forEach((key) => params.delete(key));
            FILTER_BOOLEAN_KEYS.forEach((key) => params.delete(key));

            FILTER_ARRAY_KEYS.forEach((key) => {
                const values = Array.isArray(newFilters[key]) ? newFilters[key] : [];
                values
                    .filter((value) => value !== undefined && value !== null && value !== '')
                    .forEach((value) => params.append(key, value));
            });

            FILTER_BOOLEAN_KEYS.forEach((key) => {
                if (newFilters[key]) {
                    params.set(key, 'true');
                }
            });

            params.delete('page');
        });
    }, [updateSearchParams]);

    const handleClearFilters = useCallback(() => {
        updateSearchParams((params) => {
            FILTER_ARRAY_KEYS.forEach((key) => params.delete(key));
            FILTER_BOOLEAN_KEYS.forEach((key) => params.delete(key));
            // The panel's Clear empties the search box too, and calls onSearch('') just before
            // this. Both writes start from the same URL, so this one, the last, has to drop the
            // search as well or it puts the old term back under an empty search box.
            params.delete('q');
            params.delete('page');
        });
    }, [updateSearchParams]);

    // Turning the page adds a history entry, so Back goes to the previous page of results
    // rather than out of the listing. A correction (a page past the end) replaces instead.
    const handlePaginationChange = useCallback((nextPaginationModel, { replace = false } = {}) => {
        if (nextPaginationModel.page === page) {
            // The pager reports a click on the page already showing; writing the same URL
            // again would add a history entry that Back then has to step over.
            return;
        }
        updateSearchParams((params) => {
            if (nextPaginationModel.page > 0) {
                params.set('page', String(nextPaginationModel.page + 1));
            } else {
                params.delete('page');
            }
        }, { replace });
    }, [page, updateSearchParams]);

    const handleSortModelChange = useCallback((nextSortModel) => {
        const nextSort = Array.isArray(nextSortModel) && nextSortModel.length > 0
            ? nextSortModel[0]
            : initialSortModel[0];

        updateSearchParams((params) => {
            if (nextSort?.field && nextSort?.sort && !isSameSort(nextSort, initialSortModel[0])) {
                params.set('sort', `${nextSort.field},${nextSort.sort}`);
            } else {
                params.delete('sort');
            }
            params.delete('page');
        });
    }, [initialSortModel, updateSearchParams]);

    useEffect(() => {
        const handleShowSnackbar = (event) => {
            const { message, severity } = event.detail;
            setSnackbar({ open: true, message, severity });
        };

        window.addEventListener('show-snackbar', handleShowSnackbar);
        return () => window.removeEventListener('show-snackbar', handleShowSnackbar);
    }, []);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <div className={styles.container}>
            <Grid container spacing={2} alignItems="flex-start">
                <Grid
                    item
                    xs={12}
                    md={3}
                    sx={{
                        position: { md: 'sticky' },
                        top: { md: 112 },
                        alignSelf: { md: 'flex-start' }
                    }}
                >
                    <StockFilters
                        filters={filters}
                        onFiltersChange={handleFiltersChange}
                        onClearFilters={handleClearFilters}
                        onSearch={handleSearch}
                        initialSearchValue={searchTerm}
                        suggestionFilters={filters}
                        showCategory={true}
                        showManufacturer={false}
                        showPrice={false}
                        showAvailability={true}
                        showOnSale={true}
                        showDateRange={false}
                        layout="sidebar"
                    />
                </Grid>
                <Grid item xs={12} md={9}>
                    <EnhancedStockTable 
                        filters={filters}
                        searchTerm={searchTerm}
                        displayMode="cards"
                        paginationModel={paginationModel}
                        onPaginationModelChange={handlePaginationChange}
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        tableTitle={tableTitle}
                        itemLabel={itemLabel}
                    />
                </Grid>
            </Grid>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};
